import {
  StoryFailure,
  type MarketingDecision,
  type PresenceCommand,
  type Receipt,
  type StoryAction,
  type Visit,
} from '../domain/story'
import type { StoryContext } from './ports'
import {
  automaticStoryContextReady,
  inactiveStoryContext as inactive,
  type RuntimeOptions,
} from './runtime-options'
import { PendingDelivery } from './pending-delivery'
import { retryDelay } from './request-policy'
import { AutomaticDecisionCycle } from './automatic-decision-cycle'

type ActiveStory = {
  decision: MarketingDecision
  visitId: string
  rendered: boolean
  confirmed: boolean
}
export type StorySnapshot = Readonly<{
  story: MarketingDecision | null
  visible: boolean
  busy: boolean
}>
export class StoriesRuntime {
  private context = inactive
  private clientId: string
  private sequence = 0
  private epoch = 0
  private disabled = false
  private presenceReady = false
  private visit: Visit | null = null
  private active: ActiveStory | null = null
  private readonly cycle = new AutomaticDecisionCycle()
  private selecting = false
  private listeners = new Set<() => void>()
  private snapshot: StorySnapshot = { story: null, visible: false, busy: false }
  private cancelDay: (() => void) | undefined
  private cancelExpiry: (() => void) | undefined
  private cancelPresenceRetry: (() => void) | undefined
  private cancelDecisionRetry: (() => void) | undefined
  private pendingDecisionRetry: { at: number; send: () => void } | null = null
  private visitRecoveryUsed = false
  private presenceSequence = 0
  private presenceInFlight = false
  private presenceIsVisible = false
  private connections = 0
  private cancelDetach: (() => void) | undefined
  private readonly delivery: PendingDelivery

  constructor(private readonly options: RuntimeOptions) {
    this.clientId = options.uuid()
    this.delivery = new PendingDelivery(
      options.storage,
      options.transport,
      options.clock,
      (receipt) => this.accepted(receipt),
      (id, error) => this.rejected(id, error),
    )
  }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  getSnapshot = () => this.snapshot
  isActive = () => this.active !== null || this.selecting
  attach(clearOnDetach: () => boolean) {
    this.cancelDetach?.()
    this.connections += 1
    return () => {
      this.connections -= 1
      if (this.connections === 0)
        this.cancelDetach = this.options.clock.schedule(() => {
          if (this.connections === 0) this.destroy(clearOnDetach())
        }, 0)
    }
  }
  setVisibility(visible: boolean) {
    this.update({ ...this.context, visible })
  }
  finishViewing(reservationId: string) {
    if (this.active?.decision.reservationId === reservationId) this.dismiss()
  }

  update(context: StoryContext) {
    const previous = this.context
    const accountChanged = previous.account !== context.account
    if (accountChanged) {
      this.reset()
      this.delivery.setAccount(context.account)
    }
    if (
      previous.navigationKey !== context.navigationKey ||
      (!previous.visible && context.visible) ||
      (!previous.inCabinet && context.inCabinet)
    )
      this.cycle.enter()
    this.context = context
    const wasPresent = Boolean(
      previous.account && previous.inCabinet && previous.visible,
    )
    const present = Boolean(
      context.account && context.inCabinet && context.visible,
    )
    if (
      !this.disabled &&
      context.account &&
      ((present && (accountChanged || !wasPresent)) ||
        (!present && wasPresent && !accountChanged))
    )
      this.presence(present)
    this.publish()
    this.evaluate()
  }

  wake() {
    if (this.disabled || !this.context.account) return
    this.visitRecoveryUsed = false
    this.cycle.enter()
    this.delivery.wake()
    if (this.context.visible && this.context.inCabinet) this.presence(true)
  }

  displayed(id: string) {
    const active = this.active
    if (
      !active ||
      active.decision.reservationId !== id ||
      active.rendered ||
      !this.snapshot.visible
    )
      return
    if (active.decision.expiresAt <= this.options.clock.now()) {
      this.dismiss()
      return
    }
    active.rendered = true
    this.enqueue(active, null)
  }

  act(action: StoryAction) {
    const active = this.active
    if (!active || !this.snapshot.visible) return
    active.rendered = true
    this.enqueue(active, action)
    const path = active.decision.story.ctaPath
    this.dismiss()
    if (action === 'cta' && this.options.canNavigate(path))
      this.options.navigate(path)
  }

  destroy(clearPending = false) {
    if (this.context.account && this.context.inCabinet && this.context.visible)
      this.presence(false)
    this.reset()
    this.delivery.stop()
    if (clearPending) this.delivery.setAccount(null)
    this.context = inactive
    this.publish()
  }

  private reset() {
    this.epoch += 1
    this.clientId = this.options.uuid()
    this.sequence = 0
    this.options.onVisit?.(this.clientId, null)
    this.active = null
    this.visit = null
    this.presenceReady = false
    this.presenceInFlight = false
    this.disabled = false
    this.selecting = false
    this.cycle.reset()
    this.pendingDecisionRetry = null
    this.visitRecoveryUsed = false
    this.cancelDay?.()
    this.cancelExpiry?.()
    this.cancelPresenceRetry?.()
    this.cancelDecisionRetry?.()
  }

  private presence(visible: boolean) {
    if (visible && this.presenceInFlight && this.presenceIsVisible) return
    this.presenceInFlight = true
    this.presenceIsVisible = visible
    this.presenceReady = false
    this.options.onPresencePending?.()
    const epoch = this.epoch
    this.publish()
    const command: PresenceCommand = {
      clientId: this.clientId,
      sequence: ++this.sequence,
      state: visible ? 'visible' : 'hidden',
      timezone: this.options.timezone,
    }
    this.presenceSequence = command.sequence
    this.cancelPresenceRetry?.()
    const send = async (attempt: number): Promise<void> => {
      try {
        const result = await this.options.transport.visit(command)
        if (epoch !== this.epoch || command.sequence !== this.presenceSequence)
          return
        this.presenceInFlight = false
        this.options.clock.observeServerTime?.(result.serverTime)
        if (visible) {
          if (this.active && this.active.visitId !== result.value.id)
            this.dismiss()
          this.visit = result.value
          this.options.onVisit?.(this.clientId, result.value)
          this.presenceReady = true
          this.scheduleDay(result.value)
          this.delivery.wake()
          this.publish()
          this.evaluate()
        }
      } catch (error) {
        if (epoch !== this.epoch || command.sequence !== this.presenceSequence)
          return
        this.presenceInFlight = false
        if (error instanceof StoryFailure && error.code === 'STORIES_DISABLED')
          this.disable()
        const delay = retryDelay(error, attempt)
        if (visible && delay !== null) {
          this.cancelPresenceRetry = this.options.clock.schedule(() => {
            void send(attempt + 1)
          }, delay)
        }
      }
    }
    void send(0)
  }

  private scheduleDay(visit: Visit) {
    this.cancelDay?.()
    const remaining = visit.dayEndsAt - this.options.clock.now()
    if (remaining <= 0) return
    this.cancelDay = this.options.clock.schedule(() => {
      if (this.context.visible && this.context.inCabinet) this.presence(true)
    }, remaining + 1000)
  }

  private evaluate() {
    if (!this.ready() || !this.visit || this.active) return
    if (this.selecting) {
      if (
        this.pendingDecisionRetry &&
        this.pendingDecisionRetry.at <= this.options.clock.now()
      ) {
        const retry = this.pendingDecisionRetry
        this.pendingDecisionRetry = null
        this.cancelDecisionRetry?.()
        retry.send()
      }
      return
    }
    if (!this.cycle.attempt(this.visit, this.options.clock.now())) return
    this.selecting = true
    this.publish()
    const epoch = this.epoch
    const command = {
      clientId: this.clientId,
      visitId: this.visit.id,
      platform: this.options.platform,
      key: this.options.uuid(),
    }
    const send = async (attempt: number): Promise<void> => {
      try {
        const result = await this.options.transport.decide(command)
        if (epoch !== this.epoch) return
        this.options.clock.observeServerTime?.(result.serverTime)
        this.selecting = false
        this.publish()
        if (this.visit?.id !== command.visitId) {
          this.evaluate()
          return
        }
        if (result.value.kind === 'none') return
        if (result.value.kind === 'recommendations') {
          this.options.onRecommendations?.(result.value.batch, this.clientId)
          return
        }
        const decision = result.value
        if (
          decision.expiresAt <= this.options.clock.now() ||
          !this.options.canNavigate(decision.story.ctaPath)
        )
          return
        this.active = {
          decision,
          visitId: command.visitId,
          rendered: false,
          confirmed: false,
        }
        this.cancelExpiry?.()
        this.cancelExpiry = this.options.clock.schedule(() => {
          if (
            this.active?.decision.reservationId === decision.reservationId &&
            !this.active.confirmed
          )
            this.dismiss()
        }, decision.expiresAt - this.options.clock.now())
        this.publish()
      } catch (error) {
        if (epoch !== this.epoch) return
        if (error instanceof StoryFailure && error.code === 'STORIES_DISABLED')
          this.disable()
        const wait = retryDelay(error, attempt)
        if (wait !== null) {
          this.pendingDecisionRetry = {
            at: this.options.clock.now() + wait,
            send: () => {
              void send(attempt + 1)
            },
          }
          this.cancelDecisionRetry = this.options.clock.schedule(
            () => this.evaluate(),
            wait,
          )
        } else {
          this.selecting = false
          this.publish()
          if (
            error instanceof StoryFailure &&
            error.code === 'VISIT_CHANGED' &&
            !this.visitRecoveryUsed
          ) {
            this.visitRecoveryUsed = true
            this.presence(true)
          }
        }
      }
    }
    void send(0)
  }

  private enqueue(active: ActiveStory, action: StoryAction | null) {
    if (!this.context.account) return
    this.delivery.enqueue({
      account: this.context.account,
      clientId: this.clientId,
      reservationId: active.decision.reservationId,
      action,
      deadline: active.decision.expiresAt + 23 * 3600_000,
      impressionAccepted: active.confirmed,
    })
  }

  private accepted(receipt: Receipt) {
    if (this.active?.decision.reservationId !== receipt.reservationId) return
    this.active.confirmed = true
    if (receipt.firstAction) this.dismiss()
  }

  private rejected(id: string, error: unknown) {
    if (error instanceof StoryFailure && error.code === 'STORIES_DISABLED')
      this.disable()
    if (this.active?.decision.reservationId === id) this.dismiss()
  }

  private disable() {
    this.disabled = true
    this.cancelPresenceRetry?.()
    this.cancelDecisionRetry?.()
    this.cancelDay?.()
    this.delivery.stop()
    this.dismiss()
  }
  private ready = () =>
    !this.disabled &&
    !this.options.sourceBusy?.() &&
    this.presenceReady &&
    automaticStoryContextReady(this.context)
  private dismiss() {
    this.active = null
    this.cancelExpiry?.()
    this.publish()
  }
  private publish() {
    const story = this.active?.decision ?? null
    const visible = Boolean(story && this.ready())
    if (
      this.snapshot.story === story &&
      this.snapshot.visible === visible &&
      this.snapshot.busy === this.selecting
    )
      return
    this.snapshot = { story, visible, busy: this.selecting }
    this.listeners.forEach((listener) => listener())
  }
}
